import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Funeral } from '../types';
import { PaperworkCompleteNotificationDialog } from '../components/PaperworkCompleteNotificationDialog';

type PaperworkNotificationContextValue = {
  /** Open the "paperwork complete — notify customer" dialog for a funeral. */
  notifyPaperworkComplete: (funeral: Funeral) => void;
};

const PaperworkNotificationContext =
  createContext<PaperworkNotificationContextValue | null>(null);

/**
 * Hosts a single shared `PaperworkCompleteNotificationDialog` above the funeral
 * board rows and exposes `notifyPaperworkComplete(funeral)`.
 *
 * Why a provider: the board status cells (DEC / Benefits) that trigger this
 * dialog live inside a per-row component. When the final paperwork toggle
 * completes a case, the row re-partitions from the open "Delivered" group into
 * the collapsed "Closed" group and unmounts — taking a row-local dialog with
 * it before it can paint. Hosting the dialog here, above the groups, lets it
 * survive the row moving between (or out of) groups. Mirrors
 * `FuneralDrawerProvider`.
 */
export function PaperworkNotificationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [funeral, setFuneral] = useState<Funeral | null>(null);
  const [open, setOpen] = useState(false);

  const notifyPaperworkComplete = useCallback((f: Funeral) => {
    setFuneral(f);
    setOpen(true);
  }, []);

  const value = useMemo(
    () => ({ notifyPaperworkComplete }),
    [notifyPaperworkComplete],
  );

  return (
    <PaperworkNotificationContext.Provider value={value}>
      {children}
      <PaperworkCompleteNotificationDialog
        open={open}
        onClose={() => setOpen(false)}
        funeral={funeral}
      />
    </PaperworkNotificationContext.Provider>
  );
}

/**
 * Returns the shared paperwork-notification opener, or `null` outside a
 * provider (e.g. the funeral show page / drawer sidebar, where the triggering
 * component is not unmounted and can host its own dialog).
 */
export function usePaperworkNotification(): PaperworkNotificationContextValue | null {
  return useContext(PaperworkNotificationContext);
}
